import { BIOTRICITY_SUPPORT_ADDRESS, PROGRAMS } from '@/shared/config';
import { CustomButton } from '@/shared/ui/components';
import { zeroPad } from '@/shared/lib';
import _ from 'lodash';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { NOTIFICATION_TYPES } from '../../config/notificationCenter';
import type {
  NotificationItem,
  NotificationItemCallbacks,
} from '../../lib/notificationCenter/notificationCenter.types';
import NotificationHeader from './components/notificationHeader/notificationHeader';
import NotificationWrapper from './components/notificationWrapper/notificationWrapper';
import ProgramSubscriptionMessage from './components/programSubscriptionMessage/programSubscriptionMessage';

/**
 * Shows the end-of-list marker at the bottom of the notification center.
 * Renders the No more notifications label only when the list is non-empty, loaded, not loading
 * more and at the end of data.
 *
 * @param props - The notification list and its loading-more, loaded and end-of-data flags.
 * @returns The end-of-list label, or a falsy value when it should not show.
 */
const noMoreNotificationsRenderer = ({
  notificationList,
  isLoadingMore,
  isLoadedData,
  isEndOfData,
}: {
  notificationList: NotificationItem[];
  isLoadingMore: boolean;
  isLoadedData: boolean;
  isEndOfData: boolean;
}) =>
  notificationList.length > 0 &&
  !isLoadingMore &&
  isLoadedData &&
  isEndOfData && <span className="no-more-data">{t`No more notifications`}</span>;

/**
 * Builds the title, body and facility name of one notification-center item by notification type.
 * Switches over maintenance, flagged-study, program, shipping-label, shipment, facility-setting,
 * RMA return-reminder and replacement types, building each body with action buttons wired to the
 * callbacks and marking the item read from a popup, and yields an empty object for unknown types.
 *
 * @param props - The notification item, its action callbacks, the popup flag and whether flagged
 *   studies may be viewed.
 * @returns The item's title, content and facility name, or an empty object for unknown types.
 */
const formatNotificationContent = ({
  item,
  callbacks,
  isPopup = false,
  couldViewFlaggedStudies = false,
}: {
  item: NotificationItem;
  callbacks: NotificationItemCallbacks;
  isPopup?: boolean;
  couldViewFlaggedStudies?: boolean;
}) => {
  const { csMail, email: serviceCenterEmail } = BIOTRICITY_SUPPORT_ADDRESS;
  const {
    onClickShippingLabel,
    onClickViewMaintenance,
    handleViewDetailsClick,
    handleTrackItNowClick,
    onClickNotification,
  } = callbacks;

  const popupInfo = { shouldMarkAsRead: isPopup, id: item?.id };

  switch (item.type) {
    case NOTIFICATION_TYPES.MAINTENANCE_DEVICE_COUNT: {
      const isPlural = (item.deviceCount || 0) > 1;
      return {
        title: `${item.deviceCount} ${isPlural ? 'devices need' : 'device needs'} your attention`,
        content: (
          <div className="notification-content">
            <div>
              {`There ${isPlural ? `are ${item.deviceCount} devices` : `is ${item.deviceCount} device`} ready for maintenance`}
            </div>
            <CustomButton
              className="view-details-btn notification-content-btn"
              type={isPopup ? 'primary' : 'tertiary'}
              size="small"
              onClick={() => onClickViewMaintenance?.(item, popupInfo)}
            >
              {isPlural ? t`View all` : t`View details`}
            </CustomButton>
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.NEW_ARTIFACT_STUDIES: {
      const { studyCount } = item || {};
      const isPlural = (studyCount || 0) > 1;
      return {
        title: `${studyCount} ${studyCount === 1 ? 'study needs' : 'studies need'} your attention`,
        content: (
          <div className="notification-content">
            <div>
              {`There ${isPlural ? `are ${studyCount} studies` : `is ${studyCount} study`} that may have technical issues affecting data quality.`}
            </div>
            {couldViewFlaggedStudies && (
              <CustomButton
                id="view-flagged-studies-btn"
                className="view-details-btn notification-content-btn"
                onClick={() => onClickNotification?.(popupInfo)}
                type={isPopup ? 'primary' : 'tertiary'}
                size="small"
              >
                View flagged studies
              </CustomButton>
            )}
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.PENDING_UPLOAD_FLAGGED_STUDIES: {
      const { studyCount } = item || {};
      return {
        title: `${studyCount} ${studyCount === 1 ? 'study needs' : 'studies need'} your attention`,
        content: (
          <div className="notification-content">
            <div>Studies are pending data upload due to offline device.</div>
            {couldViewFlaggedStudies && (
              <CustomButton
                id="view-flagged-studies-btn"
                className="view-details-btn notification-content-btn"
                onClick={() => onClickNotification?.(popupInfo)}
                type={isPopup ? 'primary' : 'tertiary'}
                size="small"
              >
                View flagged studies
              </CustomButton>
            )}
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.PROGRAM_SUBSCRIPTION: {
      const program = item.programName === PROGRAMS.PROTECT_PLUS ? 'Protect+' : 'Renewal Stock';
      const status = item.isProgramSubscribed ? 'subscribed' : 'unsubscribed';

      return {
        title: `${program} ${status}`,
        content: (
          <div className="notification-content">
            <ProgramSubscriptionMessage
              programName={item.programName}
              isProgramSubscribed={item.isProgramSubscribed}
              toggleSubscriptionDate={item.toggleSubscriptionDate}
              serviceCenterEmail={serviceCenterEmail}
            />
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.SHIPPING_LABEL_CREATED: {
      return {
        title: t`Shipping label created`,
        content: (
          <div className="notification-content">
            <div>
              {`The shipping label for your order ${zeroPad(item.rmaId, 5)} has just been created. Please check and download it!`}
            </div>
            <CustomButton
              id="click-here-btn"
              className="view-details-btn notification-content-btn"
              onClick={() => onClickShippingLabel?.(item.rmaTicket ?? '', popupInfo)}
              type={isPopup ? 'primary' : 'tertiary'}
              size="small"
            >
              {isPopup ? t`View` : t`View details`}
            </CustomButton>
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.REMINDER_CREATE_SHIPMENT: {
      return {
        title: t`Shipment needed`,
        content: (
          <div className="notification-content">
            <div>
              {`RMA Ticket #${item.rmaId} has been approved. Please initiate the shipment at your earliest convenience for an expedited process.`}
            </div>
            <CustomButton
              id="view-details-btn"
              className="view-details-btn notification-content-btn"
              type={isPopup ? 'primary' : 'tertiary'}
              size="small"
              onClick={() => handleViewDetailsClick?.(item.rmaTicket ?? '', popupInfo)}
            >
              {t`View details`}
            </CustomButton>
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.FACILITY_SETTING_UPDATED: {
      const settingStatus = item.canViewFlaggedStudies ? 'on' : 'off';
      return {
        title: `Flagged study is turned ${settingStatus}`,
        content: (
          <div className="notification-content">
            {item.canViewFlaggedStudies
              ? 'This feature has been enabled as per your clinic’s request. If you have any questions about this feature, '
              : 'This feature has been disabled as per your clinic’s request. If you have any questions or want to enable this feature again, '}
            please contact{' '}
            <a className="blue-text" href={`mailto:${csMail}`}>
              {csMail}
            </a>
            .
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_7_DAYS:
    case NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_21_DAYS:
    case NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_26_DAYS: {
      /**
       * Selects the reminder title and body copy for an outstanding RMA device-return notification. Branches on the item type so the 7-day and 21-day reminders each get their own wording, otherwise falls back to the urgent closes-in-4-days text, interpolating the RMA id into every message.
       *
       * @returns The notification title and message pair.
       */
      const getTitleAndMessage = () => {
        if (item.type === NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_7_DAYS) {
          return {
            title: t`Reminder: Avoid RMA Charges`,
            message: `RMA #${item.rmaId} is still open and we haven’t received the device. Please return it as soon as possible to avoid replacement charges. Need a new return label or help? We’re happy to assist. Devices not received within 30 days of approval may be billed per policy.`,
          };
        }
        if (item.type === NOTIFICATION_TYPES.REMINDER_DEVICE_RETURN_21_DAYS) {
          return {
            title: t`Action Needed: RMA Device Outstanding`,
            message: `RMA #${item.rmaId} remains open and the device hasn’t been received. Please ship it back promptly to avoid replacement charges. If you need a new label or support, contact us—we’ll make it easy. Devices not received within 30 days of approval may be billed per policy.`,
          };
        }
        return {
          title: t`Urgent: RMA Closes in 4 Days`,
          message: `RMA #${item.rmaId} is 26 days open and we still haven’t received the device. This RMA closes in 4 days; if not returned, your account will be charged per policy. Please ship immediately or contact us for a return label or assistance. Thank you.`,
        };
      };

      const { title, message } = getTitleAndMessage();

      return {
        title,
        content: (
          <div className="notification-content">
            <div>{message}</div>
            <CustomButton
              id="view-this-rma-request-btn"
              className="view-details-btn notification-content-btn"
              type={isPopup ? 'primary' : 'tertiary'}
              size="small"
              onClick={() => handleViewDetailsClick?.(item.rmaTicket ?? '', popupInfo)}
            >
              {t`View this RMA request`}
            </CustomButton>
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    case NOTIFICATION_TYPES.CREATE_LABEL_RMA_REPLACEMENT: {
      return {
        title: t`RMA #${item.rmaId} Replacement Shipped`,
        content: (
          <div className="notification-content">
            <div>{t`Your replacement is on its way!`}</div>
            <CustomButton
              id="track-it-now-btn"
              className="view-details-btn notification-content-btn"
              type={isPopup ? 'primary' : 'tertiary'}
              size="small"
              onClick={() => handleTrackItNowClick?.(item?.rmaTicket ?? '', popupInfo)}
            >
              {t`Track it now`}
            </CustomButton>
          </div>
        ),
        facilityName: item.facilityName,
      };
    }

    default: {
      return {};
    }
  }
};

/**
 * Renders one notification row of the notification drawer. Runs the item through formatNotificationContent to obtain its title, content and facility name, returns an empty div when no title was produced, and otherwise wraps a NotificationHeader plus the facility name and content inside NotificationWrapper.
 *
 * @param item - The notification to render.
 * @param callbacks - Action handlers wired into the rendered content, such as opening an RMA or the flagged studies.
 * @param couldViewFlaggedStudies - Whether flagged-study actions may be offered in the content.
 * @returns The notification row element, or an empty div when the item has no title.
 */
const renderNotificationItem = (
  item: NotificationItem,
  callbacks: NotificationItemCallbacks,
  couldViewFlaggedStudies: boolean,
) => {
  switch (item.type) {
    default: {
      const { title, content, facilityName } = formatNotificationContent({
        item,
        callbacks,
        couldViewFlaggedStudies,
      }) as {
        title: React.ReactNode;
        content: React.ReactNode;
        facilityName: string;
      };
      if (!title) return <div />;
      return (
        <NotificationWrapper item={item}>
          <NotificationHeader item={item} title={title} />
          <div className="notification__body">
            <div className="facility-name">{facilityName}</div>
            {content}
          </div>
        </NotificationWrapper>
      );
    }
  }
};

/**
 * Renders the loading placeholder of the notification drawer. Maps a range of ten entries to skeleton cards, each holding one header bar and three content bars.
 *
 * @returns The list of skeleton placeholder elements.
 */
const skeletonRender = () =>
  _.map(_.range(0, 10), () => (
    <div className="notification-item-skeleton-container">
      <div className="header-container">
        <Skeleton width="100%" height="100%" />
      </div>
      <div className="content-container">
        <Skeleton width="100%" height="100%" />
      </div>
      <div className="content-container">
        <Skeleton width="100%" height="100%" />
      </div>
      <div className="content-container">
        <Skeleton width="100%" height="100%" />
      </div>
    </div>
  ));

export {
  formatNotificationContent,
  noMoreNotificationsRenderer,
  renderNotificationItem,
  skeletonRender,
};
